/** @jsxImportSource @emotion/react */
import { nanoid } from "nanoid";
import React, { Fragment, useCallback, useMemo, useState } from "react";
import Api from "../../utilities/Api";
import Fnaa, { FnaaRaw } from "../../utilities/Fnaa";
import { QuotationResponses } from "../../utilities/Quotations";
import useBooleanState from "../../utilities/useBooleanState";
import useFieldCss from "../../utilities/useFieldCss";
import Icon from "../Icon";
import SelectorHelp, { SelectorHelpProps } from "./Help";

export type ImmatSelectorProps = {
  placeholder?: string;
  required?: boolean;
  label?: string;
  value: FnaaRaw | null | undefined;
  onChange: (value: FnaaRaw | null) => any;
  responses: QuotationResponses;
} & SelectorHelpProps;

export default function ImmatSelector(props: ImmatSelectorProps) {
  const { value, onChange, placeholder, required, label, responses } = props;

  const fieldCss = useFieldCss();

  const parsed = useMemo(() => {
    if (value) return Fnaa.parse(value);
    return value;
  }, [value]);

  const id = useMemo(nanoid, []);

  const [loading, setLoading] = useBooleanState(false);
  const [invalid, setInvalid] = useBooleanState(false);
  const [inputValue, setInputValue] = useState<string>("");
  const [focused, setFocused] = useBooleanState(false);

  const onInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue(e.target.value);
    },
    []
  );

  const onFocus = useCallback(() => {
    setFocused.toTrue();
    if (parsed) {
      setInputValue(parsed.immatriculation);
      onChange(null);
    } else {
      setInvalid.toFalse();
    }
  }, [parsed]);

  const onBlur = useCallback(async () => {
    setFocused.toFalse();
    if (!parsed && inputValue) {
      try {
        setLoading.toTrue();
        setInvalid.toFalse();
        const data = await Api.getFnaaInfos(inputValue, responses);
        onChange(data);
      } catch (err) {
        setInvalid.toTrue();
      } finally {
        setLoading.toFalse();
      }
    }
  }, [parsed, inputValue]);

  const displayedValue = useMemo(() => {
    if (parsed)
      return `✅ ${parsed.marque} ${parsed.modele} ${parsed.version} (${parsed.immatriculation})`;
    else {
      if (inputValue) {
        if (loading) {
          return `⏳ Détection du véhicule en cours... (${inputValue})`;
        } else if (invalid) {
          return `⚠️ Immatriculation invalide : ${inputValue}`;
        }
      } else {
        return "";
      }
    }
    return inputValue;
  }, [inputValue, parsed, loading, invalid]);

  const containerCss = focused
    ? fieldCss.inputContainerFocus
    : fieldCss.inputContainer;

  const inputCss = focused ? fieldCss.inputFocus : fieldCss.input;

  return (
    <Fragment>
      <label htmlFor={id} css={fieldCss.label}>
        {label}
      </label>
      <div css={containerCss}>
        <input
          id={id}
          css={inputCss}
          type={"text"}
          required={required}
          placeholder={placeholder}
          value={displayedValue}
          onChange={onInputChange}
          onFocus={onFocus}
          onBlur={onBlur}
          disabled={loading}
        />
        <div
          css={{
            alignSelf: "center",
            display: focused ? "block" : "none",
            color: "black",
          }}
        >
          <Icon name="check" size={24} />
        </div>
      </div>
      <SelectorHelp {...props} />
    </Fragment>
  );
}
